import { AliasType, EntityStatus, EntitySubmissionType } from '../gql/graphql';

import { graphql } from '../gql';
import { toPagination } from './utils';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

export const GET_ENTITIES = graphql(`
  query getEntities(
    $pagination: PaginationRequest
    $sort: PaginationSort
    $filters: EntityFilterInput
    $includeDetailed: Boolean = true
  ) {
    entities(pagination: $pagination, sort: $sort, filters: $filters) {
      edges {
        cursor
        node {
          uuid
          referenceId
          type
          status
          agency
          createdAt
          updatedAt
          fundamentals @include(if: $includeDetailed) {
            edges {
              cursor
              node {
                uuid
                employees
                primary
                sources
                revenue
              }
            }
          }
          industrySectors @include(if: $includeDetailed) {
            edges {
              cursor
              node {
                uuid
                name
                naicsCode
                sources
                primary
              }
            }
          }
          aliases(filters: { source: PRIMARY, type: LEGAL_NAME }) {
            edges {
              cursor
              queryCursor
              node {
                uuid
                name
                type
                sources
                primary
              }
            }
          }
          addresses @include(if: $includeDetailed) {
            edges {
              cursor
              node {
                uuid
                street1
                street2
                city
                type
                postalCode
                source
                regions {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                countries {
                  edges {
                    cursor
                    node {
                      uuid
                      name
                      iso2Code
                    }
                  }
                }
                primary
              }
            }
          }
        }
      }
      pageInfo {
        totalResults
        hasNextPage
        hasPreviousPage
        pageNumber
        pageSize
      }
    }
  }
`);
export interface IUseEntitiesQueryArg {
  pageNumber?: number;
  pageSize?: number;
  entityType?: EntitySubmissionType;
  isInsurer?: boolean;
  isAgency?: boolean;
  isCapacityProvider?: boolean;
  isProgramPartner?: boolean;
  submissionFinalized?: boolean;
  enabled?: boolean;
  sort?: { field: string; order: string };
  primaryIndustrySectorUuids?: string[];
  insuredNameUuids?: string[];
  statuses?: EntityStatus[];
  insuredRevenueRange?: { min: number; max: number };
}
export function useGetEntities({
  pageNumber,
  pageSize,
  entityType,
  isInsurer,
  isAgency,
  isCapacityProvider,
  isProgramPartner,
  submissionFinalized,
  enabled = true,
  sort,
  primaryIndustrySectorUuids,
  insuredNameUuids,
  statuses,
  insuredRevenueRange,
}: IUseEntitiesQueryArg) {
  const { loading, error, data } = useQuery(GET_ENTITIES, {
    variables: {
      pagination: toPagination(pageNumber, pageSize),
      sort: sort ? { key: sort.field, order: sort.order } : undefined,
      filters: {
        submissionType: entityType,
        insurer: isInsurer,
        agency: isAgency,
        capacityProvider: isCapacityProvider,
        programPartner: isProgramPartner,
        submissionFinalized: submissionFinalized,
        primaryIndustrySectorUuids: primaryIndustrySectorUuids,
        insuredNameUuids: insuredNameUuids,
        statuses: statuses,
        insuredRevenueRange: insuredRevenueRange,
      },
      includeDetailed: !isProgramPartner,
    },
    fetchPolicy: 'no-cache',
    skip: !enabled,
  });

  const entities = useMemo(() => {
    if (!data || !data.entities || !data.entities.edges) {
      return [];
    }
    return data.entities.edges.map((edge) => {
      const address = edge.node.addresses?.edges?.find(
        (address) => address.node.primary
      )?.node;
      const brokerInfo: IBrokerEntityInfo = {
        licenseNumber: 'N/A',
        activeAgents: 'N/A',
        defaultCommission: 'N/A',
        averagePremium: 'N/A',
        commissionEarned: 'N/A',
        totalPremium: 'N/A',
        totalSubmissions: 'N/A',
      };
      return {
        uuid: edge.node.uuid,
        referenceId: edge.node.referenceId ?? '',
        legalName:
          edge.node.aliases?.edges?.find(
            (alias) =>
              alias.node.type === AliasType.LegalName && alias.node.primary
          )?.node.name ?? '',

        name:
          edge.node.aliases?.edges?.find(
            (alias) =>
              alias.node.type === AliasType.LegalName && alias.node.primary
          )?.node.name ??
          edge.node.aliases?.edges?.find((alias) => alias.node.primary)?.node
            .name ??
          edge.node.aliases?.edges?.find(
            (alias) => alias.node.type === AliasType.LegalName
          )?.node.name ??
          edge.node.aliases?.edges?.find(
            (alias) => alias.node.type === AliasType.TradingName
          )?.node.name ??
          edge.node.aliases?.edges?.find(
            (alias) => alias.node.type === AliasType.TradingName
          )?.node.name ??
          edge.node.aliases?.edges?.[0]?.node.name,
        address1: address?.street1 ?? '',
        address2: address?.street2 ?? '',
        city: address?.city ?? '',
        state: (address?.regions?.edges?.[0]?.node?.iso2Code ?? '').replace(
          /^[^-]+-/,
          ''
        ),
        postalCode: address?.postalCode ?? '',
        country: address?.countries?.edges?.[0]?.node?.iso2Code ?? '',
        status: edge.node.status ?? '',
        createdAt: edge.node.createdAt ?? '',
        updatedAt: edge.node.updatedAt ?? '',
        revenue:
          edge.node.fundamentals?.edges?.find(
            (fundamental) => fundamental.node.primary
          )?.node?.revenue ?? 0,
        naicsCode:
          edge.node.industrySectors?.edges?.find(
            (industrySector) => industrySector.node.primary
          )?.node?.naicsCode ?? '',
        industrySector:
          edge.node.industrySectors?.edges?.find(
            (industrySector) => industrySector.node.primary
          )?.node?.name ?? '',
        documentsLink: 'to be implemented',
        data: edge,

        // TODO: Move these fields to some type of transformer specifically for brokers
        brokerInfo,
      };
    });
  }, [data]);

  return {
    error,
    loading,
    data,
    entities,
  };
}

export type EntityDetails = ReturnType<typeof useGetEntities>['entities'][0];

export interface IBrokerEntityInfo {
  licenseNumber: string;
  activeAgents: number | string;
  defaultCommission: number | string;
  averagePremium: number | string;
  totalSubmissions: number | string;
  totalPremium: number | string;
  commissionEarned: number | string;
}
